import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import debounce from "lodash/debounce";
import { useQueryClient } from "@tanstack/react-query";
import ContentContainer from "../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import ReloadButton from "../../ui-components/reload-button/reloadButton";
import { useSelector } from "react-redux";
import { userSelector } from "../../slices/user/userSlice";
import { PAGE_IDS } from "../../app/data/common/pageIds";
import { QUERY_KEYS } from "../../app/data/common/queryKeys";
import { UserUtils } from "../../app/data/user/userUtils";
import XGSErrorMessage from "../../ui-components/error-message/errorMessage";
import {
  OutboundPlanningGridRow,
  OutboundPlanningMetrics,
  useOutboundPlanningGridData,
} from "./useOutboundPlanningGridData";
import TerminalCard from "../../ui-components/terminal-card/terminalCard";
import LabeledSelectInput from "../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "../../ui-components/molecules/labeled-inputs/labeledInput";
import { XGSSelectOption } from "../../ui-components/xgs-select/xgsSelect";
import TerminalSummaryService from "../../app/data/terminal-summary/terminalsummaryService";
import { TerminalSummaryModel } from "../../app/data/terminal-summary/TerminalListModel";
import {
  DEFAULT_FREIGHT_INCLUDES,
  DEFAULT_SORT,
  FreightGroupKey,
  HubDefinition,
  MetricColumn,
  SortKey,
  SortState,
  SortableMetricGroup,
  buildGroupedSections,
  computeAdjustedTotals,
  computeHubSectionTotals,
  computeIncludedPickupsTotal,
  getPickupsAggregateState,
  GRID_COLUMN_COUNT,
  sortKeysEqual,
} from "./outboundPlanningGridUtils";
import { ReactComponent as ArrowUpIcon } from "../../images/arrow_up.svg";
import { ReactComponent as ArrowDownIcon } from "../../images/arrow_down.svg";
import OutboundPlanningShipmentDrawer from "./components/outboundPlanningShipmentDrawer";
import { ShipmentListCategory, ShipmentListContext, createShipmentListFetchId } from "./shipmentListTypes";
import { useHubGroups } from "./hooks/useHubGroups";
import { HubGroupDto } from "../../app/data/hub-groups/models";
import "./linehaulOutboundPlanning.scss";
import "./outboundPlanningList.scss";

const OUTBOUND_PLANNING_AUTO_REFRESH_MS = 300_000;
const OUTBOUND_PLANNING_RELATIVE_TIME_TICK_MS = 30_000;
const LINEHAUL_ADMINS_GROUP = "Linehaul Admins";

function MetricCells(props: {
  m: OutboundPlanningMetrics;
  dimmed?: boolean;
  category?: ShipmentListCategory;
  row?: OutboundPlanningGridRow;
  onShipmentsClick?: (category: ShipmentListCategory, row: OutboundPlanningGridRow) => void;
}) {
  const { m, dimmed, category, row, onShipmentsClick } = props;
  const cellClass = dimmed ? "linehaul-outbound-planning__cell--dimmed" : undefined;
  const canClickShipments =
    category != null && row != null && onShipmentsClick != null && m.shipments > 0;

  return (
    <>
      <td className={cellClass}>
        {canClickShipments ? (
          <button
            type="button"
            className="linehaul-outbound-planning__shipments-link xgs-table-link-cell"
            onClick={() => onShipmentsClick(category, row)}
          >
            {m.shipments.toLocaleString()}
          </button>
        ) : (
          m.shipments.toLocaleString()
        )}
      </td>
      <td className={cellClass}>{m.pieces.toLocaleString()}</td>
      <td className={cellClass}>{m.weight.toLocaleString()}</td>
    </>
  );
}

function HubMetricCells(props: {
  m: OutboundPlanningMetrics;
  dimmed?: boolean;
  category?: ShipmentListCategory;
  hubName: string;
  rows: OutboundPlanningGridRow[];
  onHubShipmentsClick?: (
    category: ShipmentListCategory,
    hubName: string,
    rows: OutboundPlanningGridRow[]
  ) => void;
}) {
  const { m, dimmed, category, hubName, rows, onHubShipmentsClick } = props;
  const cellClass = dimmed ? "linehaul-outbound-planning__cell--dimmed" : undefined;
  const canClickShipments =
    category != null && onHubShipmentsClick != null && m.shipments > 0 && rows.length > 0;

  return (
    <>
      <td className={cellClass}>
        {canClickShipments ? (
          <button
            type="button"
            className="linehaul-outbound-planning__shipments-link xgs-table-link-cell"
            onClick={() => onHubShipmentsClick(category, hubName, rows)}
          >
            {m.shipments.toLocaleString()}
          </button>
        ) : (
          m.shipments.toLocaleString()
        )}
      </td>
      <td className={cellClass}>{m.pieces.toLocaleString()}</td>
      <td className={cellClass}>{m.weight.toLocaleString()}</td>
    </>
  );
}

function GroupHeaderWithCheckbox(props: {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onChange: (next: boolean) => void;
}) {
  const { label, checked, indeterminate, onChange } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  return (
    <div className="linehaul-outbound-planning__group-header">
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={`Include ${label} in totals`}
      />
      <span>{label}</span>
    </div>
  );
}

function toHubTerminalSelectOption(terminal: TerminalSummaryModel): XGSSelectOption {
  const airportCode = terminal.airportCode || "";
  const terminalName = terminal.terminalName || "";
  const terminalNumber = terminal.terminalNumber;
  return {
    value: String(terminalNumber),
    label: `${airportCode} - ${terminalName} ${terminalNumber}`.trim(),
    valueForSearch: `${airportCode}|${terminalName}|${terminalNumber}`,
  };
}

function hubTerminalOptionFromNumber(
  terminalNumber: number,
  rows: OutboundPlanningGridRow[],
  member?: { terminalName: string | null; airportCode: string | null }
): XGSSelectOption {
  if (member) {
    const airportCode = member.airportCode || "";
    const terminalName = member.terminalName || "";
    const label =
      airportCode || terminalName
        ? `${airportCode} - ${terminalName} ${terminalNumber}`.trim()
        : `Terminal ${terminalNumber}`;
    return {
      value: String(terminalNumber),
      label,
      valueForSearch: `${airportCode}|${terminalName}|${terminalNumber}`,
    };
  }
  const row = rows.find((r) => r.destinationTerminalNumber === terminalNumber);
  if (row) {
    return {
      value: String(terminalNumber),
      label: `${row.airportCode} - ${row.terminalName} ${terminalNumber}`,
      valueForSearch: `${row.airportCode}|${row.terminalName}|${terminalNumber}`,
    };
  }
  return {
    value: String(terminalNumber),
    label: `Terminal ${terminalNumber}`,
    valueForSearch: String(terminalNumber),
  };
}

function selectedTerminalNumbersFromOptions(options: XGSSelectOption[]): number[] {
  return options
    .map((option) => Number(option.value))
    .filter((terminalNumber) => Number.isFinite(terminalNumber));
}

function formatHubTerminalLabel(parts: {
  airportCode?: string | null;
  terminalName?: string | null;
  terminalNumber: number;
}): string {
  const code = parts.airportCode?.trim() || "";
  const name = parts.terminalName?.trim() || "";
  const namePart = [code, name].filter(Boolean).join(" - ");
  return namePart ? `${namePart} ${parts.terminalNumber}` : String(parts.terminalNumber);
}

function formatHubTerminalsLabel(
  hub: HubDefinition,
  hubDtos: HubGroupDto[],
  rows: OutboundPlanningGridRow[] = []
): string {
  if (!hub.terminalNumbers.length) return "No terminals assigned.";
  const dto = hubDtos.find((h) => h.id === hub.id);
  const membersByNumber = new Map(
    (dto?.terminals ?? []).map((t) => [t.terminalNumber, t])
  );

  return hub.terminalNumbers
    .slice()
    .sort((a, b) => a - b)
    .map((terminalNumber) => {
      const member = membersByNumber.get(terminalNumber);
      if (member) {
        return formatHubTerminalLabel({
          airportCode: member.airportCode,
          terminalName: member.terminalName,
          terminalNumber,
        });
      }
      const row = rows.find((r) => r.destinationTerminalNumber === terminalNumber);
      if (row) {
        return formatHubTerminalLabel({
          airportCode: row.airportCode,
          terminalName: row.terminalName,
          terminalNumber,
        });
      }
      return String(terminalNumber);
    })
    .join(", ");
}

function HubTerminalAsyncSelect(props: {
  value: XGSSelectOption[];
  onChange: (next: XGSSelectOption[]) => void;
}) {
  const { value, onChange } = props;
  const [isLoading, setIsLoading] = useState(false);

  const loadTerminalOptions = useMemo(
    () =>
      debounce((query: string, callback: (options: XGSSelectOption[]) => void) => {
        const search = query?.trim() || null;
        setIsLoading(true);
        TerminalSummaryService.getInstance()
          .getTerminalsList(0, search, null, null, null, null)
          .then((res) => {
            if (res.isCancel?.()) return;
            if (typeof res.ok === "function" && !res.ok()) {
              callback([]);
              return;
            }
            const content = Array.isArray(res?.data?.content) ? res.data.content : [];
            callback(content.map(toHubTerminalSelectOption));
          })
          .catch(() => callback([]))
          .finally(() => setIsLoading(false));
      }, 300),
    []
  );

  return (
    <LabeledSelectInput
      label="Terminals"
      labelMode={LabelModes.column}
      placeholder="Search by airport code, name, or number..."
      isMulti
      isClearable
      async
      cacheOptions={false}
      defaultOptions
      isLoading={isLoading}
      options={[]}
      value={value}
      loadOptions={loadTerminalOptions}
      onMultiValuesChange={(options) => onChange(options ?? [])}
      className="linehaul-outbound-planning__hub-terminal-select"
      menuPlacement="auto"
      menuPortalTarget={document.body}
      menuPosition="fixed"
      closeMenuOnScroll={(e) => {
        const target = e.target as HTMLElement;
        // Keep open while scrolling the options list itself
        return !target?.closest?.(".xgs-select__menu");
      }}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 1100 }),
      }}
    />
  );
}

function SortIcons(props: { isSorted: false | "asc" | "desc" }) {
  const { isSorted } = props;
  const showUp = isSorted === "asc" || !isSorted;
  const showDown = isSorted === "desc" || !isSorted;
  return (
    <span className="linehaul-outbound-planning__sort-icons">
      {showUp && (
        <span className="linehaul-outbound-planning__sort-icon">
          <ArrowUpIcon />
        </span>
      )}
      {showDown && (
        <span className="linehaul-outbound-planning__sort-icon">
          <ArrowDownIcon />
        </span>
      )}
    </span>
  );
}

function SortableHeader(props: {
  label: React.ReactNode;
  sortKey: SortKey;
  sort: SortState;
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
}) {
  const { label, sortKey, sort, onSort, align = "right" } = props;
  const isActive = sortKeysEqual(sort.key, sortKey);
  const isSorted: false | "asc" | "desc" = isActive ? sort.direction : false;
  return (
    <button
      type="button"
      className={`linehaul-outbound-planning__sortable linehaul-outbound-planning__sortable--${align}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="linehaul-outbound-planning__sortable-label">{label}</span>
      <SortIcons isSorted={isSorted} />
    </button>
  );
}

function MetricHeaderTrio(props: {
  group: SortableMetricGroup;
  sort: SortState;
  onSort: (key: SortKey) => void;
}) {
  const { group, sort, onSort } = props;
  const make = (metric: MetricColumn, label: string) => (
    <th>
      <SortableHeader label={label} sortKey={{ kind: "metric", group, metric }} sort={sort} onSort={onSort} />
    </th>
  );
  return (
    <>
      {make("shipments", "Shipments")}
      {make("pieces", "Pieces")}
      {make("weight", "Weight")}
    </>
  );
}

function PlanningRow(props: {
  row: OutboundPlanningGridRow;
  adjustedTotals: OutboundPlanningMetrics;
  included: Record<FreightGroupKey, boolean>;
  onShipmentsClick: (category: ShipmentListCategory, row: OutboundPlanningGridRow) => void;
}) {
  const { row, adjustedTotals, included, onShipmentsClick } = props;

  return (
    <tr>
      <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--1">
        <TerminalCard
          terminalNumber={row.destinationTerminalNumber}
          terminalName={row.terminalName}
          terminalAirportCode={row.airportCode}
        />
      </td>
      <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--2">
        {adjustedTotals.shipments > 0 ? (
          <button
            type="button"
            className="linehaul-outbound-planning__shipments-link xgs-table-link-cell"
            onClick={() => onShipmentsClick("all", row)}
          >
            {adjustedTotals.shipments.toLocaleString()}
          </button>
        ) : (
          adjustedTotals.shipments.toLocaleString()
        )}
      </td>
      <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--3">
        {adjustedTotals.pieces.toLocaleString()}
      </td>
      <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--4">
        {adjustedTotals.weight.toLocaleString()}
      </td>
      <MetricCells
        m={row.inHouse}
        dimmed={!included.inHouse}
        category="inHouse"
        row={row}
        onShipmentsClick={onShipmentsClick}
      />
      <MetricCells
        m={row.inbound}
        dimmed={!included.inbound}
        category="inbound"
        row={row}
        onShipmentsClick={onShipmentsClick}
      />
      <MetricCells
        m={computeIncludedPickupsTotal(row, included)}
        dimmed={
          !included.pickupsUnassigned && !included.pickupsAssigned && !included.pickupsCompleted
        }
        category="pickupsAggregate"
        row={row}
        onShipmentsClick={onShipmentsClick}
      />
      <MetricCells
        m={row.pickupsUnassigned}
        dimmed={!included.pickupsUnassigned}
        category="pickupsUnassigned"
        row={row}
        onShipmentsClick={onShipmentsClick}
      />
      <MetricCells
        m={row.pickupsAssigned}
        dimmed={!included.pickupsAssigned}
        category="pickupsAssigned"
        row={row}
        onShipmentsClick={onShipmentsClick}
      />
      <MetricCells
        m={row.pickupsCompleted}
        dimmed={!included.pickupsCompleted}
        category="pickupsCompleted"
        row={row}
        onShipmentsClick={onShipmentsClick}
      />
    </tr>
  );
}

export const LinehaulOutboundPlanning: React.FC = () => {
  const userState = useSelector(userSelector);
  const originId = userState.activeTerminal?.id;
  const queryClient = useQueryClient();
  const { rows, isLoading, isFetching, error, enabled } = useOutboundPlanningGridData();
  const canManageHubGroups = UserUtils.hasAssignedGroup(
    userState.profile,
    LINEHAUL_ADMINS_GROUP
  );
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [, setRelativeTimeTick] = useState(0);
  const didSetInitialRefreshRef = useRef(false);

  const refreshOutboundPlanning = useCallback(async () => {
    if (!enabled || typeof originId !== "number") return;
    setIsRefreshing(true);
    try {
      // Invalidate only this page's summary queries for the active terminal (avoids
      // racing unrelated OUTBOUND_PLANNING-prefixed caches and overlapping refetches).
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.OUTBOUND_PLANNING, "inHouseFreight", originId],
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.OUTBOUND_PLANNING, "outboundInboundFreight", originId],
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.OUTBOUND_PLANNING, QUERY_KEYS.PICKUP_SUMMARY, originId],
        }),
      ]);
      setLastRefreshedAt(Date.now());
    } finally {
      setIsRefreshing(false);
    }
  }, [enabled, originId, queryClient]);

  useEffect(() => {
    didSetInitialRefreshRef.current = false;
    setLastRefreshedAt(null);
  }, [originId]);

  useEffect(() => {
    if (!enabled || error) return;
    if (!isLoading && !isFetching && !didSetInitialRefreshRef.current) {
      didSetInitialRefreshRef.current = true;
      setLastRefreshedAt(Date.now());
    }
  }, [enabled, error, isLoading, isFetching]);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => {
      void refreshOutboundPlanning();
    }, OUTBOUND_PLANNING_AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [enabled, refreshOutboundPlanning]);

  useEffect(() => {
    if (lastRefreshedAt == null) return;
    const id = window.setInterval(() => {
      setRelativeTimeTick((n) => n + 1);
    }, OUTBOUND_PLANNING_RELATIVE_TIME_TICK_MS);
    return () => clearInterval(id);
  }, [lastRefreshedAt]);

  const lastRefreshedLabel =
    lastRefreshedAt == null ? null : moment(lastRefreshedAt).fromNow();

  const [freightIncludes, setFreightIncludes] =
    useState<Record<FreightGroupKey, boolean>>(DEFAULT_FREIGHT_INCLUDES);

  const [shipmentListContext, setShipmentListContext] = useState<ShipmentListContext | null>(null);

  const handleShipmentsClick = useCallback(
    (category: ShipmentListCategory, row: OutboundPlanningGridRow) => {
      setShipmentListContext({
        category,
        destinationTerminalNumbers: [row.destinationTerminalNumber],
        destinationTerminalName: row.terminalName,
        destinationTerminalAirportCode: row.airportCode,
        fetchId: createShipmentListFetchId(),
      });
    },
    []
  );

  const handleHubShipmentsClick = useCallback(
    (
      category: ShipmentListCategory,
      hubName: string,
      hubRows: OutboundPlanningGridRow[]
    ) => {
      setShipmentListContext({
        category,
        destinationTerminalNumbers: hubRows.map((r) => r.destinationTerminalNumber),
        hubName,
        fetchId: createShipmentListFetchId(),
      });
    },
    []
  );

  const handleCloseShipmentDrawer = useCallback(() => {
    setShipmentListContext(null);
  }, []);

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);

  const handleSort = useCallback((key: SortKey) => {
    setSort((prev) => {
      if (sortKeysEqual(prev.key, key)) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      const initialDir = key.kind === "destination" ? "asc" : "desc";
      return { key, direction: initialDir };
    });
  }, []);

  const {
    hubs,
    hubDtos,
    isLoading: hubsLoading,
    error: hubsError,
    isMutating: hubsMutating,
    createHub,
    updateHub,
    deleteHub,
  } = useHubGroups(typeof originId === "number" ? originId : undefined);

  const [hubPanelOpen, setHubPanelOpen] = useState(false);
  const [newHubName, setNewHubName] = useState("");
  const [selectedForNewHub, setSelectedForNewHub] = useState<XGSSelectOption[]>([]);
  const [editingHubId, setEditingHubId] = useState<string | null>(null);
  const [editHubName, setEditHubName] = useState("");
  const [selectedForEditHub, setSelectedForEditHub] = useState<XGSSelectOption[]>([]);

  useEffect(() => {
    setSelectedForNewHub([]);
    setNewHubName("");
    setEditingHubId(null);
    setEditHubName("");
    setSelectedForEditHub([]);
  }, [originId]);

  const setInclude = useCallback((key: FreightGroupKey, value: boolean) => {
    setFreightIncludes((prev) => ({ ...prev, [key]: value }));
  }, []);

  /** "Pickups" header is a select-all/deselect-all UI for the 3 sub-types. */
  const setAllPickups = useCallback((value: boolean) => {
    setFreightIncludes((prev) => ({
      ...prev,
      pickupsUnassigned: value,
      pickupsAssigned: value,
      pickupsCompleted: value,
    }));
  }, []);

  const pickupsAggregate = useMemo(
    () => getPickupsAggregateState(freightIncludes),
    [freightIncludes]
  );

  const sections = useMemo(
    () => buildGroupedSections(rows, hubs, sort, freightIncludes),
    [rows, hubs, sort, freightIncludes]
  );

  const showSectionHeaders =
    sections.length > 1 || sections.some((s) => s.hubId !== null && s.rows.length > 0);

  const addHub = useCallback(async () => {
    if (!canManageHubGroups) return;
    const name = newHubName.trim();
    const terminalNumbers = selectedTerminalNumbersFromOptions(selectedForNewHub);
    if (!name || terminalNumbers.length === 0 || hubsMutating) return;
    try {
      await createHub({ name, terminalNumbers });
      setNewHubName("");
      setSelectedForNewHub([]);
    } catch {
      /* toast handled in mutation */
    }
  }, [canManageHubGroups, createHub, hubsMutating, newHubName, selectedForNewHub]);

  const removeHub = useCallback(
    async (hubId: string) => {
      if (!canManageHubGroups || hubsMutating) return;
      if (editingHubId === hubId) {
        setEditingHubId(null);
        setEditHubName("");
        setSelectedForEditHub([]);
      }
      try {
        await deleteHub(hubId);
      } catch {
        /* toast handled in mutation */
      }
    },
    [canManageHubGroups, deleteHub, editingHubId, hubsMutating]
  );

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "linehaul_outbound_planning" });
  }, [userState.activeTerminal?.id]);

  const startEditingHub = useCallback(
    (hub: HubDefinition) => {
      if (!canManageHubGroups) return;
      const dto = hubDtos.find((h) => h.id === hub.id);
      const membersByNumber = new Map(
        (dto?.terminals ?? []).map((m) => [m.terminalNumber, m])
      );
      setEditingHubId(hub.id);
      setEditHubName(hub.name);
      setSelectedForEditHub(
        hub.terminalNumbers.map((n) =>
          hubTerminalOptionFromNumber(n, rows, membersByNumber.get(n))
        )
      );
    },
    [canManageHubGroups, hubDtos, rows]
  );

  const cancelEditingHub = useCallback(() => {
    setEditingHubId(null);
    setEditHubName("");
    setSelectedForEditHub([]);
  }, []);

  const saveHubEdits = useCallback(async () => {
    if (!canManageHubGroups) return;
    const name = editHubName.trim();
    const terminalNumbers = selectedTerminalNumbersFromOptions(selectedForEditHub);
    if (!editingHubId || !name || terminalNumbers.length === 0 || hubsMutating) return;

    try {
      await updateHub({ id: editingHubId, request: { name, terminalNumbers } });
      setEditingHubId(null);
      setEditHubName("");
      setSelectedForEditHub([]);
    } catch {
      /* toast handled in mutation */
    }
  }, [canManageHubGroups, editHubName, editingHubId, hubsMutating, selectedForEditHub, updateHub]);

  return (
    <ContentContainer
      mods={{ "full-width": true }}
      titleComponent={
        <ContentContainerToolbar
          title="Outbound Planning"
          pageId={PAGE_IDS.LINEHAUL_OUTBOUND_PLANNING}
          isLoading={isLoading}
          mods={{ "full-width": true }}
        >
          <div className="linehaul-outbound-planning__refresh-toolbar">
            <span className="linehaul-outbound-planning__last-refreshed">
              Last refreshed {lastRefreshedLabel ?? "—"}
            </span>
            <ReloadButton
              title="Refresh"
              spinner={isRefreshing}
              disabled={isRefreshing || !enabled}
              onClick={() => {
                void refreshOutboundPlanning();
              }}
            />
          </div>
        </ContentContainerToolbar>
      }
    >
      {!enabled && (
        <p className="linehaul-outbound-planning__hint">Select an active terminal to load outbound planning data.</p>
      )}
      {enabled && error && <XGSErrorMessage>Error loading outbound planning data. Please try again.</XGSErrorMessage>}
      {enabled && !error && rows.length === 0 && !isLoading && (
        <div className="linehaul-outbound-planning__empty">No outbound freight found for this terminal.</div>
      )}
      {enabled && !error && rows.length > 0 && (
        <>
          <div className="linehaul-outbound-planning__toolbar">
            <button
              type="button"
              className="linehaul-outbound-planning__hub-toggle"
              onClick={() => setHubPanelOpen((v) => !v)}
            >
              {hubPanelOpen ? "Hide hub groups" : "Destination hub groups"}
              {hubs.length > 0 ? ` (${hubs.length})` : ""}
            </button>
          </div>

          {hubPanelOpen && (
            <div className="linehaul-outbound-planning__hub-panel">
              <p className="linehaul-outbound-planning__hub-help">
                {canManageHubGroups
                  ? "Group destinations that share a linehaul hub (hub-and-spoke). Terminals can only belong to one hub; assigning moves them from another hub."
                  : "Destination hub groups for linehaul hub-and-spoke planning. Contact a Linehaul Admin to create or edit groups."}
              </p>
              {hubsError && (
                <XGSErrorMessage>Error loading hub groups. Please try again.</XGSErrorMessage>
              )}
              {hubsLoading && !hubsError && hubs.length === 0 && (
                <p className="linehaul-outbound-planning__hint">Loading hub groups…</p>
              )}
              {canManageHubGroups && (
                <div className="linehaul-outbound-planning__hub-add">
                  <div className="linehaul-outbound-planning__hub-add-row">
                    <input
                      type="text"
                      className="linehaul-outbound-planning__hub-name-input"
                      placeholder="New hub name"
                      value={newHubName}
                      onChange={(e) => setNewHubName(e.target.value)}
                      disabled={hubsMutating}
                    />
                    <button
                      type="button"
                      className="linehaul-outbound-planning__btn-primary"
                      onClick={() => {
                        void addHub();
                      }}
                      disabled={
                        hubsMutating || !newHubName.trim() || selectedForNewHub.length === 0
                      }
                    >
                      Create hub
                    </button>
                  </div>
                  <HubTerminalAsyncSelect
                    value={selectedForNewHub}
                    onChange={setSelectedForNewHub}
                  />
                </div>
              )}
              <div className="linehaul-outbound-planning__hub-cards">
                {hubs.map((hub) => (
                  <div key={hub.id} className="linehaul-outbound-planning__hub-card">
                    {canManageHubGroups && editingHubId === hub.id ? (
                      <>
                        <div className="linehaul-outbound-planning__hub-add-row">
                          <input
                            type="text"
                            className="linehaul-outbound-planning__hub-name-input"
                            placeholder="Hub name"
                            value={editHubName}
                            onChange={(e) => setEditHubName(e.target.value)}
                            disabled={hubsMutating}
                          />
                          <button
                            type="button"
                            className="linehaul-outbound-planning__btn-primary"
                            onClick={() => {
                              void saveHubEdits();
                            }}
                            disabled={
                              hubsMutating || !editHubName.trim() || selectedForEditHub.length === 0
                            }
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="linehaul-outbound-planning__link-btn"
                            onClick={cancelEditingHub}
                            disabled={hubsMutating}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="linehaul-outbound-planning__link-btn"
                            onClick={() => {
                              void removeHub(hub.id);
                            }}
                            disabled={hubsMutating}
                          >
                            Remove hub
                          </button>
                        </div>
                        <HubTerminalAsyncSelect
                          value={selectedForEditHub}
                          onChange={setSelectedForEditHub}
                        />
                      </>
                    ) : (
                      <>
                        <div className="linehaul-outbound-planning__hub-card-head">
                          <strong>{hub.name}</strong>
                          {canManageHubGroups && (
                            <div className="linehaul-outbound-planning__hub-card-actions">
                              <button
                                type="button"
                                className="linehaul-outbound-planning__link-btn"
                                onClick={() => startEditingHub(hub)}
                                disabled={hubsMutating}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="linehaul-outbound-planning__link-btn"
                                onClick={() => {
                                  void removeHub(hub.id);
                                }}
                                disabled={hubsMutating}
                              >
                                Remove hub
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="linehaul-outbound-planning__hub-terminals">
                          {formatHubTerminalsLabel(hub, hubDtos, rows)}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="linehaul-outbound-planning__scroll">
            <table className="linehaul-outbound-planning__table" role="grid">
              <colgroup>
                <col className="linehaul-outbound-planning__col--destination" />
                <col />
                <col />
                <col />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
              </colgroup>
              <thead>
                <tr>
                  <th rowSpan={2} className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--1">
                    <SortableHeader
                      label="Destination terminal"
                      sortKey={{ kind: "destination" }}
                      sort={sort}
                      onSort={handleSort}
                      align="left"
                    />
                  </th>
                  <th colSpan={3} className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--totals">
                    Totals
                  </th>
                  <th colSpan={3}>
                    <GroupHeaderWithCheckbox
                      label="In-house freight"
                      checked={freightIncludes.inHouse}
                      onChange={(v) => setInclude("inHouse", v)}
                    />
                  </th>
                  <th colSpan={3}>
                    <GroupHeaderWithCheckbox
                      label="Inbound freight"
                      checked={freightIncludes.inbound}
                      onChange={(v) => setInclude("inbound", v)}
                    />
                  </th>
                  <th colSpan={3}>
                    <GroupHeaderWithCheckbox
                      label="Pickups"
                      checked={pickupsAggregate.allChecked}
                      indeterminate={pickupsAggregate.indeterminate}
                      onChange={(v) => setAllPickups(v)}
                    />
                  </th>
                  <th colSpan={3}>
                    <GroupHeaderWithCheckbox
                      label="Pickups — Unassigned"
                      checked={freightIncludes.pickupsUnassigned}
                      onChange={(v) => setInclude("pickupsUnassigned", v)}
                    />
                  </th>
                  <th colSpan={3}>
                    <GroupHeaderWithCheckbox
                      label="Pickups — Assigned"
                      checked={freightIncludes.pickupsAssigned}
                      onChange={(v) => setInclude("pickupsAssigned", v)}
                    />
                  </th>
                  <th colSpan={3}>
                    <GroupHeaderWithCheckbox
                      label="Pickups — Completed"
                      checked={freightIncludes.pickupsCompleted}
                      onChange={(v) => setInclude("pickupsCompleted", v)}
                    />
                  </th>
                </tr>
                <tr>
                  <th className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--2">
                    <SortableHeader
                      label="Shipments"
                      sortKey={{ kind: "metric", group: "totals", metric: "shipments" }}
                      sort={sort}
                      onSort={handleSort}
                    />
                  </th>
                  <th className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--3">
                    <SortableHeader
                      label="Pieces"
                      sortKey={{ kind: "metric", group: "totals", metric: "pieces" }}
                      sort={sort}
                      onSort={handleSort}
                    />
                  </th>
                  <th className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--4">
                    <SortableHeader
                      label="Weight"
                      sortKey={{ kind: "metric", group: "totals", metric: "weight" }}
                      sort={sort}
                      onSort={handleSort}
                    />
                  </th>
                  <MetricHeaderTrio group="inHouse" sort={sort} onSort={handleSort} />
                  <MetricHeaderTrio group="inbound" sort={sort} onSort={handleSort} />
                  <MetricHeaderTrio group="pickupsAggregate" sort={sort} onSort={handleSort} />
                  <MetricHeaderTrio group="pickupsUnassigned" sort={sort} onSort={handleSort} />
                  <MetricHeaderTrio group="pickupsAssigned" sort={sort} onSort={handleSort} />
                  <MetricHeaderTrio group="pickupsCompleted" sort={sort} onSort={handleSort} />
                </tr>
              </thead>
              {sections.map((section) => {
                const sectionTotals = computeHubSectionTotals(section.rows, freightIncludes);
                const labelText = section.hubName + (section.rows.length > 0 ? ` · ${section.rows.length} terminal${section.rows.length === 1 ? "" : "s"}` : "");
                return (
                  <tbody key={section.hubId ?? "other"}>
                    {showSectionHeaders && (
                      <tr className="linehaul-outbound-planning__section-label-row">
                        <td colSpan={GRID_COLUMN_COUNT}>
                          <span className="linehaul-outbound-planning__section-label-text">{labelText}</span>
                        </td>
                      </tr>
                    )}
                    {showSectionHeaders && (
                      <tr className="linehaul-outbound-planning__section-totals-row">
                        <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--1">
                          <span className="linehaul-outbound-planning__hub-totals-label">Hub totals</span>
                        </td>
                        <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--2">
                          {sectionTotals.totals.shipments > 0 ? (
                            <button
                              type="button"
                              className="linehaul-outbound-planning__shipments-link xgs-table-link-cell"
                              onClick={() =>
                                handleHubShipmentsClick("all", section.hubName, section.rows)
                              }
                            >
                              {sectionTotals.totals.shipments.toLocaleString()}
                            </button>
                          ) : (
                            sectionTotals.totals.shipments.toLocaleString()
                          )}
                        </td>
                        <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--3">
                          {sectionTotals.totals.pieces.toLocaleString()}
                        </td>
                        <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--4">
                          {sectionTotals.totals.weight.toLocaleString()}
                        </td>
                        <HubMetricCells
                          m={sectionTotals.inHouse}
                          dimmed={!freightIncludes.inHouse}
                          category="inHouse"
                          hubName={section.hubName}
                          rows={section.rows}
                          onHubShipmentsClick={handleHubShipmentsClick}
                        />
                        <HubMetricCells
                          m={sectionTotals.inbound}
                          dimmed={!freightIncludes.inbound}
                          category="inbound"
                          hubName={section.hubName}
                          rows={section.rows}
                          onHubShipmentsClick={handleHubShipmentsClick}
                        />
                        <HubMetricCells
                          m={sectionTotals.pickupsTotal}
                          dimmed={pickupsAggregate.noneChecked}
                          category="pickupsAggregate"
                          hubName={section.hubName}
                          rows={section.rows}
                          onHubShipmentsClick={handleHubShipmentsClick}
                        />
                        <HubMetricCells
                          m={sectionTotals.pickupsUnassigned}
                          dimmed={!freightIncludes.pickupsUnassigned}
                          category="pickupsUnassigned"
                          hubName={section.hubName}
                          rows={section.rows}
                          onHubShipmentsClick={handleHubShipmentsClick}
                        />
                        <HubMetricCells
                          m={sectionTotals.pickupsAssigned}
                          dimmed={!freightIncludes.pickupsAssigned}
                          category="pickupsAssigned"
                          hubName={section.hubName}
                          rows={section.rows}
                          onHubShipmentsClick={handleHubShipmentsClick}
                        />
                        <HubMetricCells
                          m={sectionTotals.pickupsCompleted}
                          dimmed={!freightIncludes.pickupsCompleted}
                          category="pickupsCompleted"
                          hubName={section.hubName}
                          rows={section.rows}
                          onHubShipmentsClick={handleHubShipmentsClick}
                        />
                      </tr>
                    )}
                    {section.rows.map((row) => (
                      <PlanningRow
                        key={row.destinationTerminalNumber}
                        row={row}
                        adjustedTotals={computeAdjustedTotals(row, freightIncludes)}
                        included={freightIncludes}
                        onShipmentsClick={handleShipmentsClick}
                      />
                    ))}
                  </tbody>
                );
              })}
            </table>
          </div>
        </>
      )}
      <OutboundPlanningShipmentDrawer
        context={shipmentListContext}
        originTerminalNumber={originId}
        onClose={handleCloseShipmentDrawer}
      />
    </ContentContainer>
  );
};
